import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { getAdminPost, upsertPost, deletePost, type AdminBlogPost } from "@/lib/admin-blog.functions";

export const Route = createFileRoute("/_authenticated/admin/blog/$id")({
  head: () => ({
    meta: [
      { title: "Redigera inlägg — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BlogEdit,
});

type Form = {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  category: string;
  cover_emoji: string;
  status: "draft" | "published";
  author_name: string;
  author_role: string;
  read_minutes: number;
  seo_title: string | null;
  seo_description: string | null;
  title_en: string | null;
  excerpt_en: string | null;
  body_en: string | null;
  seo_title_en: string | null;
  seo_description_en: string | null;
  translation_status_en: "pending" | "translated" | "skipped";
  published_at?: string;
};

const EMPTY: Form = {
  slug: "",
  title: "",
  excerpt: "",
  body: "",
  category: "Strategi",
  cover_emoji: "🎬",
  status: "draft",
  author_name: "Fenny-teamet",
  author_role: "Norrhavet Group",
  read_minutes: 6,
  seo_title: "",
  seo_description: "",
  title_en: "",
  excerpt_en: "",
  body_en: "",
  seo_title_en: "",
  seo_description_en: "",
  translation_status_en: "pending",
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/å|ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function fromPost(p: AdminBlogPost): Form {
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    body: p.body,
    category: p.category,
    cover_emoji: p.cover_emoji,
    status: (p.status === "published" ? "published" : "draft"),
    author_name: p.author_name,
    author_role: p.author_role,
    read_minutes: p.read_minutes || 6,
    seo_title: p.seo_title,
    seo_description: p.seo_description,
    title_en: p.title_en,
    excerpt_en: p.excerpt_en,
    body_en: p.body_en,
    seo_title_en: p.seo_title_en,
    seo_description_en: p.seo_description_en,
    translation_status_en: (p.translation_status_en as Form["translation_status_en"]) || "pending",
    published_at: p.published_at,
  };
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-white/50 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-white/40 mt-1">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-white/30";

function BlogEdit() {
  const { id } = Route.useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const getFn = useServerFn(getAdminPost);
  const saveFn = useServerFn(upsertPost);
  const delFn = useServerFn(deletePost);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "blog", id],
    queryFn: () => getFn({ data: { id } }),
    enabled: !isNew,
  });

  const [form, setForm] = useState<Form>(EMPTY);
  const [slugDirty, setSlugDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.post) {
      setForm(fromPost(data.post));
      setSlugDirty(true);
    }
  }, [data]);

  const update = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // Auto-generate slug from title while user hasn't touched slug (new posts only)
  useEffect(() => {
    if (isNew && !slugDirty && form.title) {
      setForm((p) => ({ ...p, slug: slugify(p.title) }));
    }
  }, [form.title, isNew, slugDirty]);

  const validationErrors = useMemo(() => {
    const e: string[] = [];
    if (!form.title.trim()) e.push("Titel saknas");
    if (!form.slug.trim()) e.push("Slug saknas");
    if (!/^[a-z0-9-]+$/.test(form.slug)) e.push("Slug får bara innehålla a-z, 0-9 och bindestreck");
    if (!form.excerpt.trim()) e.push("Ingress saknas");
    if (!form.body.trim()) e.push("Brödtext saknas");
    if (!form.category.trim()) e.push("Kategori saknas");
    if (!form.cover_emoji.trim()) e.push("Cover emoji saknas");
    if (!Number.isFinite(form.read_minutes) || form.read_minutes < 1) e.push("Lästid måste vara minst 1");
    if (form.read_minutes > 120) e.push("Lästid max 120");
    return e;
  }, [form]);

  const handleSave = async () => {
    setErr(null);
    setSaved(false);
    if (validationErrors.length) {
      setErr(validationErrors.join(" • "));
      return;
    }
    setSaving(true);
    try {
      // Build a clean payload that matches the server zod schema exactly.
      const base = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        body: form.body,
        category: form.category.trim(),
        cover_emoji: form.cover_emoji.trim(),
        status: form.status,
        author_name: form.author_name.trim() || "Fenny-teamet",
        author_role: form.author_role.trim() || "Norrhavet Group",
        read_minutes: Math.max(1, Math.min(120, Math.round(form.read_minutes))),
        seo_title: form.seo_title?.trim() ? form.seo_title.trim() : null,
        seo_description: form.seo_description?.trim() ? form.seo_description.trim() : null,
        title_en: form.title_en?.trim() ? form.title_en.trim() : null,
        excerpt_en: form.excerpt_en?.trim() ? form.excerpt_en.trim() : null,
        body_en: form.body_en?.trim() ? form.body_en : null,
        seo_title_en: form.seo_title_en?.trim() ? form.seo_title_en.trim() : null,
        seo_description_en: form.seo_description_en?.trim() ? form.seo_description_en.trim() : null,
        translation_status_en: form.translation_status_en,
      };
      const payload: Record<string, unknown> = { ...base };
      if (!isNew && form.id) payload.id = form.id;
      // If publishing for the first time, stamp published_at to now
      if (form.status === "published") {
        payload.published_at = form.published_at || new Date().toISOString();
      } else if (form.published_at) {
        payload.published_at = form.published_at;
      }

      const res = await saveFn({ data: payload as never });
      qc.invalidateQueries({ queryKey: ["admin", "blog"] });
      qc.invalidateQueries({ queryKey: ["admin", "blog", id] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (isNew) navigate({ to: "/admin/blog/$id", params: { id: res.id } });
    } catch (e) {
      console.error("[admin.blog] save failed", e);
      setErr(e instanceof Error ? e.message : "Något gick fel vid sparning");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!form.id) return;
    if (!confirm(`Radera "${form.title}"? Detta kan inte ångras.`)) return;
    try {
      await delFn({ data: { id: form.id } });
      qc.invalidateQueries({ queryKey: ["admin", "blog"] });
      navigate({ to: "/admin/blog" });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kunde inte radera");
    }
  };

  // Cmd/Ctrl + S to save
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (!isNew && isLoading) {
    return <div className="p-6 md:p-8 text-white/60">Laddar inlägg…</div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl space-y-6">
      <Link to="/admin/blog" className="text-sm text-white/60 hover:text-white">← Tillbaka</Link>
      <header className="flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10 bg-background/70 backdrop-blur py-2 -mx-2 px-2 rounded-lg">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isNew ? "Nytt inlägg" : form.title || "Redigera inlägg"}
          </h1>
          <p className="text-xs text-white/40 mt-0.5">
            {form.status === "published" ? "Publicerad" : "Utkast"}
            {form.published_at && ` • ${new Date(form.published_at).toLocaleString("sv-SE")}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saved && <span className="text-xs text-emerald-300">Sparat ✓</span>}
          {!isNew && (
            <button
              onClick={handleDelete}
              className="rounded-full border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 px-4 py-2 text-sm"
            >
              Radera
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-mint px-5 py-2 text-sm font-medium text-mint-foreground disabled:opacity-50"
          >
            {saving ? "Sparar…" : "Spara (⌘S)"}
          </button>
        </div>
      </header>

      {err && (
        <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-200 whitespace-pre-wrap">
          {err}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Titel">
          <input className={inputCls} value={form.title} onChange={(e) => update("title", e.target.value)} />
        </Field>
        <Field label="Slug" hint="a-z, 0-9, bindestreck">
          <input
            className={inputCls}
            value={form.slug}
            onChange={(e) => {
              setSlugDirty(true);
              update("slug", e.target.value);
            }}
          />
        </Field>
        <Field label="Kategori"><input className={inputCls} value={form.category} onChange={(e) => update("category", e.target.value)} /></Field>
        <Field label="Cover emoji"><input className={inputCls} value={form.cover_emoji} onChange={(e) => update("cover_emoji", e.target.value)} /></Field>
        <Field label="Status">
          <select className={inputCls} value={form.status} onChange={(e) => update("status", e.target.value as Form["status"])}>
            <option value="draft">Utkast</option>
            <option value="published">Publicerad</option>
          </select>
        </Field>
        <Field label="Lästid (min)">
          <input
            type="number"
            min={1}
            max={120}
            className={inputCls}
            value={form.read_minutes}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              update("read_minutes", Number.isFinite(n) ? n : 1);
            }}
          />
        </Field>
        <Field label="Författare"><input className={inputCls} value={form.author_name} onChange={(e) => update("author_name", e.target.value)} /></Field>
        <Field label="Författar-roll"><input className={inputCls} value={form.author_role} onChange={(e) => update("author_role", e.target.value)} /></Field>
      </div>

      <Field label={`Ingress (${form.excerpt.length}/2000)`}>
        <textarea rows={3} className={inputCls} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} />
      </Field>
      <Field label="Brödtext (markdown)">
        <textarea rows={20} className={`${inputCls} font-mono text-xs`} value={form.body} onChange={(e) => update("body", e.target.value)} />
      </Field>

      <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 pt-4">SEO</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="SEO-titel"><input className={inputCls} value={form.seo_title ?? ""} onChange={(e) => update("seo_title", e.target.value)} /></Field>
        <Field label="SEO-beskrivning"><input className={inputCls} value={form.seo_description ?? ""} onChange={(e) => update("seo_description", e.target.value)} /></Field>
      </div>

      <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 pt-4">Engelsk översättning</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Titel (EN)"><input className={inputCls} value={form.title_en ?? ""} onChange={(e) => update("title_en", e.target.value)} /></Field>
        <Field label="Status (EN)">
          <select className={inputCls} value={form.translation_status_en} onChange={(e) => update("translation_status_en", e.target.value as Form["translation_status_en"])}>
            <option value="pending">pending</option>
            <option value="translated">translated</option>
            <option value="skipped">skipped</option>
          </select>
        </Field>
      </div>
      <Field label="Ingress (EN)"><textarea rows={3} className={inputCls} value={form.excerpt_en ?? ""} onChange={(e) => update("excerpt_en", e.target.value)} /></Field>
      <Field label="Brödtext (EN)"><textarea rows={14} className={`${inputCls} font-mono text-xs`} value={form.body_en ?? ""} onChange={(e) => update("body_en", e.target.value)} /></Field>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="SEO-titel (EN)"><input className={inputCls} value={form.seo_title_en ?? ""} onChange={(e) => update("seo_title_en", e.target.value)} /></Field>
        <Field label="SEO-beskrivning (EN)"><input className={inputCls} value={form.seo_description_en ?? ""} onChange={(e) => update("seo_description_en", e.target.value)} /></Field>
      </div>
    </div>
  );
}
